// ─── Opened DMs (client-only, additive) ─────────────────────────────
// The DM sidebar section now shows only conversations you've actually OPENED
// (Slack/Telegram-style) instead of every workspace member. This set records
// which DM peers the user has opened; the sidebar unions it with has-unread +
// favorite + the active peer so an incoming DM or a starred peer still surfaces.
//
// Purely CLIENT-SIDE (mirrors FavoritesState): persisted to localStorage keyed
// by the bound user id, never sent to the relay, so it's fully additive with no
// server/schema change. Stored shape: workspaceId → Set<peerUserId>.

const OPENED_DMS_BASE = "cyborg7_opened_dms";
// A new member's suggested DMs (seedSuggestions): each peer is opened AND tagged with this
// prefix (the row's quiet "Say hi"), and the sentinel says it ran, so it never runs twice.
const SUGGESTED_PREFIX = "suggested:";
const SUGGESTED_SENTINEL = "\0dm-suggested";
export const DM_SUGGESTIONS_MAX = 6;

// Parse the persisted { workspaceId: peerId[] } blob into a Set-per-workspace
// map. Extracted with guard clauses so bindUser stays shallow (the inline
// version tripped oxlint's max-depth). Returns null on any malformed shape.
function parseOpened(raw: string): Record<string, Set<string>> | null {
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const next: Record<string, Set<string>> = {};
  for (const [ws, ids] of Object.entries(parsed as Record<string, unknown>)) {
    if (Array.isArray(ids)) {
      next[ws] = new Set(ids.filter((id): id is string => typeof id === "string"));
    }
  }
  return next;
}

export class OpenedDmsState {
  // workspaceId → Set<peerUserId>. $state object of Sets; every mutation
  // reassigns the object so Svelte's runes observe the change (matches
  // FavoritesState / UnreadFlagState).
  private _opened: Record<string, Set<string>> = $state({});
  private _boundUserId: string | null = null;

  // null while unbound — see FavoritesState.storageKey for why the old fallback
  // to the unsuffixed base key was residue worth removing.
  private get storageKey(): string | null {
    return this._boundUserId ? `${OPENED_DMS_BASE}_${this._boundUserId}` : null;
  }

  bindUser(userId: string): void {
    this._boundUserId = userId;
    this._opened = {};
    if (typeof localStorage === "undefined") return;
    const key = this.storageKey;
    if (!key) return;
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? parseOpened(raw) : null;
      if (parsed) this._opened = parsed;
    } catch {
      // intentional: best-effort hydrate of opened DMs; falls back to empty.
    }
  }

  clearLocal(): void {
    this._boundUserId = null;
    this._opened = {};
  }

  // Seed a workspace's opened-set from its existing DM-activity peers the FIRST
  // time we see it (no stored entry yet), so conversations that predate this
  // feature don't vanish from the list on rollout. A workspace with a stored
  // entry — even an empty one — is left alone so a user's explicit removals are
  // never undone. `byWorkspace` = dmActivityState.snapshot().
  seedFromActivity(byWorkspace: Record<string, readonly string[]>): void {
    let changed = false;
    const next = { ...this._opened };
    for (const [ws, peers] of Object.entries(byWorkspace)) {
      if (ws in next) continue; // already tracked → respect prior removals
      next[ws] = new Set(peers);
      changed = true;
    }
    if (changed) {
      this._opened = next;
      this.persist();
    }
  }

  // Record a workspace as TRACKED without opening anything, so seedFromActivity
  // above treats it as "already seen" from here on.
  //
  // seedFromActivity's first-run test is `ws in next` — keyed on the opened map
  // itself, which never gains an entry for a workspace with no local DM
  // activity. That leaves the one-time migration re-arming on EVERY boot for
  // such a workspace, so any writer into dmActivityState that runs before the
  // next boot's migration is read as "conversations that predate the feature"
  // and promoted into the visible list. Server-seeded DM recency
  // (DmActivityState.seedActivity) is the first such writer: 90 days of
  // relay-side truth, most of it conversations this device never opened. Marking
  // the workspace tracked at the same moment the recency is written makes the
  // migration structurally unable to read it — this boot or any later one.
  markSeeded(workspaceId: string): void {
    if (!workspaceId || workspaceId in this._opened) return;
    this._opened = { ...this._opened, [workspaceId]: new Set<string>() };
    this.persist();
  }

  // A new member's first rows, ONCE per workspace on this device: a dismissed suggestion never
  // comes back. Only THIS user's own list: nothing is sent and no recency is written, so no one
  // else's list (or this one's next boot migration) can grow from it.
  seedSuggestions(workspaceId: string, peerIds: readonly string[]): void {
    if (!workspaceId || !this._boundUserId || this.has(workspaceId, SUGGESTED_SENTINEL)) return;
    const next = new Set(this._opened[workspaceId] ?? []);
    const peers = peerIds.filter(
      (id) => typeof id === "string" && !!id && id !== "new" && !id.startsWith("\0"),
    );
    for (const id of [...new Set(peers)].slice(0, DM_SUGGESTIONS_MAX)) {
      next.add(id);
      next.add(SUGGESTED_PREFIX + id);
    }
    next.add(SUGGESTED_SENTINEL);
    this._opened = { ...this._opened, [workspaceId]: next };
    this.persist();
  }

  /** The suggested peers, in the order they were suggested. */
  suggested(workspaceId: string): string[] {
    const ids = [...(this._opened[workspaceId] ?? [])];
    return ids
      .filter((id) => id.startsWith(SUGGESTED_PREFIX))
      .map((id) => id.slice(SUGGESTED_PREFIX.length));
  }

  has(workspaceId: string, peerId: string): boolean {
    return this._opened[workspaceId]?.has(peerId) ?? false;
  }

  add(workspaceId: string, peerId: string): void {
    // CYBORG-688: "new" is the compose-new-DM placeholder route segment, never a
    // real peer — don't add it to the opened set (mirrors the write-side guard in
    // pg.markDmRead). A self-DM (peerId === userId) is a real feature; NOT rejected.
    if (!workspaceId || !peerId || peerId === "new") return;
    const ws = this._opened[workspaceId] ?? new Set<string>();
    if (ws.has(peerId)) return;
    const next = new Set(ws);
    next.add(peerId);
    this._opened = { ...this._opened, [workspaceId]: next };
    this.persist();
  }

  remove(workspaceId: string, peerId: string): void {
    const ws = this._opened[workspaceId];
    if (!ws?.has(peerId)) return;
    const next = new Set(ws);
    next.delete(peerId);
    this._opened = { ...this._opened, [workspaceId]: next };
    this.persist();
  }

  private persist(): void {
    if (typeof localStorage === "undefined") return;
    const key = this.storageKey;
    if (!key) return; // unbound (signed out) ⇒ no owner to write under.
    try {
      const serializable: Record<string, string[]> = {};
      for (const [ws, ids] of Object.entries(this._opened)) serializable[ws] = [...ids];
      localStorage.setItem(key, JSON.stringify(serializable));
    } catch {
      // intentional: best-effort persistence of opened DMs; not user-facing.
    }
  }
}

export const openedDmsState = new OpenedDmsState();
